import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../components/ui/Alert';
import { useBoard, useRuleSet } from '../../features/board/api';
import { SoloBoardScreen } from '../../features/board/solo/SoloBoardScreen';
import { writeLastSession } from '../../features/session/lastSession';
import { SessionStoreProvider } from '../../features/session/SessionStoreProvider';
import { SoloLifecycleBanner } from '../../features/session/SoloLifecycleBanner';
import { useSession } from '../../features/session/useSession';
import { GameLayout } from '../../layouts/GameLayout';
import { errorKeyFor } from '../../lib/errors';
import { isLocale } from '../../lib/i18n';

/**
 * Spec 12.2 `/:lang/solo/:sessionId — solo AI board`.
 *
 * NO SOCKET, deliberately. Spec 6.7: "solo sessions use plain HTTP + SSE and open no
 * WebSocket at all. There is one client, no fan-out, and no presence to track — a socket
 * would buy nothing and cost a connection." Note the absence of `SessionSocketProvider`
 * here, which the facilitated board at `play.$sessionId.tsx` does wrap itself in.
 */
export const Route = createFileRoute('/$lang/solo/$sessionId')({ component: Page });

function Page(): React.ReactNode {
  const { lang, sessionId } = Route.useParams();

  /* A pointer for the next visit, in case the tab is closed and the backstop sweep abandons
   * this session before the guard could (ADR — solo auto-abandon on idle/exit). */
  useEffect(() => {
    writeLastSession({ sessionId, mode: 'solo', lang });
  }, [sessionId, lang]);

  return (
    <GameLayout lang={lang}>
      {/* The store is created here so it is scoped to this session and dies with the route. */}
      <SessionStoreProvider sessionId={sessionId}>
        {/* ADR — solo auto-abandon on idle/exit. Inside the store so the guard can read
            status and drive the abandon/resume commands; a non-modal strip like the
            facilitated ConnectionBanner, so the board stays readable underneath. */}
        <SoloLifecycleBanner sessionId={sessionId} lang={lang} />
        <SoloSession sessionId={sessionId} lang={lang} />
      </SessionStoreProvider>
    </GameLayout>
  );
}

function SoloSession({
  sessionId,
  lang,
}: {
  readonly sessionId: string;
  readonly lang: string;
}): React.ReactNode {
  const { t } = useTranslation(['common', 'board']);
  const locale = isLocale(lang) ? lang : 'en';

  const { session, error } = useSession(sessionId);
  /*
   * Spec 4.7: "The board renders its static 72 squares immediately and hydrates tokens when
   * state arrives." Both queries are issued together rather than chained, so the board is not
   * waiting on the session — the board id only pins a session to a version.
   */
  const board = useBoard(locale, session?.boardId);
  const ruleSet = useRuleSet(session?.ruleSetId);

  if (error !== null && error !== undefined) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Alert tone="error">{t(errorKeyFor(error))}</Alert>
      </div>
    );
  }

  if (board.data === undefined || session === null) {
    return <SoloLoading label={t('board:solo.loading')} />;
  }

  return (
    <SoloBoardScreen session={session} board={board.data} ruleSet={ruleSet.data} lang={locale} />
  );
}

/**
 * The board-assembling state (design "Mode A Solo", loading). Surface and shimmer only — the
 * shapes match the final header / board / rail so the swap does not reflow, the same argument
 * spec 4.7 makes for text. The one line of copy is a serif whisper, not a spinner label.
 */
function SoloLoading({ label }: { readonly label: string }): React.ReactNode {
  return (
    <div
      className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 lg:p-8"
      role="status"
      aria-label={label}
    >
      <div className="h-6 animate-pulse rounded bg-surface-raised/50" />
      <div className="flex gap-8">
        <div className="hidden min-w-0 flex-1 flex-col items-center gap-6 lg:flex">
          <div className="aspect-square w-full max-w-3xl animate-pulse rounded bg-surface-raised/60" />
          <p className="font-display text-xl font-light italic text-ink-muted">{label}</p>
        </div>
        <div className="hidden w-full max-w-sm flex-col gap-3 lg:flex">
          <div className="h-4 w-2/3 animate-pulse rounded bg-surface-raised/50" />
          <div className="h-24 animate-pulse rounded bg-surface-raised/40" />
        </div>
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}
